import React, { useCallback, useMemo, useRef } from "react";

import { Dropdown, FormField } from "@prequel/shared-components";

import { DELIVERY_METHODS, DeliveryMethod, styleDeliveryMethod, URL_PLACEHOLDERS, Webhook } from "@/types";

const FIXED_URL_DELIVERY_METHODS: DeliveryMethod[] = ["pagerduty", "datadog"];
const API_KEY_DELIVERY_METHODS: DeliveryMethod[] = ["pagerduty", "datadog"];
const API_KEY_REQUIRED_DELIVERY_METHODS: DeliveryMethod[] = ["pagerduty", "datadog"];

const DROPDOWN_ITEMS: { key: DeliveryMethod; text: React.JSX.Element }[] = Object.keys(DELIVERY_METHODS).map((key) => ({
  key: key as DeliveryMethod,
  text: styleDeliveryMethod(key as DeliveryMethod),
}));

type WebhookDeliveryConfigProps = {
  webhook: Webhook;
  setWebhookField: (key: keyof Webhook, value: string) => void;
  onDeliveryMethodChange: (deliveryMethod: DeliveryMethod) => void;
  isEditing: boolean;
  disabled: boolean;
};

const WebhookDeliveryConfig = ({
  webhook,
  setWebhookField,
  onDeliveryMethodChange,
  isEditing: isEditing,
  disabled,
}: WebhookDeliveryConfigProps) => {
  const selectedDeliveryMethod = useMemo(
    () => webhook.vendor || (Object.keys(DELIVERY_METHODS)[0] as DeliveryMethod),
    [webhook.vendor],
  );

  const originalUrlRef = useRef(webhook.url);

  const urlPlaceholder = useMemo(
    () => URL_PLACEHOLDERS[selectedDeliveryMethod as keyof typeof URL_PLACEHOLDERS] || URL_PLACEHOLDERS.default,
    [selectedDeliveryMethod],
  );

  const urlSubtext = useMemo(
    () =>
      FIXED_URL_DELIVERY_METHODS.includes(selectedDeliveryMethod)
        ? `The URL to send webhook events to. ${DELIVERY_METHODS[selectedDeliveryMethod]} has a fixed URL that cannot be modified.`
        : "The URL to send webhook events to.",
    [selectedDeliveryMethod],
  );

  const urlPattern = useMemo(() => {
    switch (selectedDeliveryMethod) {
      case "slack":
        return "^https://hooks\\.slack\\.com/services/.+$";
      default:
        return "https?://.*";
    }
  }, [selectedDeliveryMethod]);

  const urlPatternComment = useMemo(() => {
    switch (selectedDeliveryMethod) {
      case "slack":
        return "Slack webhook URL must start with https://hooks.slack.com/services/";
      case "generic_post":
      case "generic_get":
      default:
        return "URL must start with http:// or https://";
    }
  }, [selectedDeliveryMethod]);

  const handleDeliveryMethodChange = useCallback(
    (selectedMethod: { key: DeliveryMethod; text: React.JSX.Element }) => {
      const deliveryMethod = selectedMethod.key;
      onDeliveryMethodChange(deliveryMethod);
      setWebhookField("vendor", deliveryMethod);

      if (FIXED_URL_DELIVERY_METHODS.includes(deliveryMethod)) {
        if (!FIXED_URL_DELIVERY_METHODS.includes(webhook.vendor)) {
          // Only save the original URL if coming from a non-fixed URL method
          originalUrlRef.current = webhook.url;
        }
        // Set the URL placeholder for fixed URL methods
        setWebhookField("url", URL_PLACEHOLDERS[deliveryMethod]);
      } else if (FIXED_URL_DELIVERY_METHODS.includes(webhook.vendor)) {
        // Restore the original URL if switching from a fixed URL method to a non-fixed URL method
        setWebhookField("url", originalUrlRef.current);
      }
      // If switching between non-fixed URL methods, do nothing with the URL
    },
    [onDeliveryMethodChange, setWebhookField, webhook.url, webhook.vendor],
  );

  const showApiKeyField = useMemo(
    () => API_KEY_DELIVERY_METHODS.includes(selectedDeliveryMethod),
    [selectedDeliveryMethod],
  );

  const requireApiKey = useMemo(
    () => API_KEY_REQUIRED_DELIVERY_METHODS.includes(selectedDeliveryMethod) && !isEditing,
    [selectedDeliveryMethod, isEditing],
  );

  const apiKeySubtext = useMemo(
    () =>
      isEditing
        ? "The API key to use when sending the webhook. Leave blank to keep the existing API key."
        : "The API key to use when sending the webhook",
    [isEditing],
  );

  return (
    <>
      <Dropdown
        label="Delivery Method"
        items={DROPDOWN_ITEMS}
        selectedItem={DROPDOWN_ITEMS.find((method) => method.key === selectedDeliveryMethod)}
        setSelectedItem={handleDeliveryMethodChange}
        subtext="Delivery method (vendor)"
        disabled={isEditing || disabled}
      />
      <FormField
        label="URL"
        id="url"
        type="url"
        pattern={urlPattern}
        patternComment={urlPatternComment}
        placeholder={urlPlaceholder}
        subtext={urlSubtext}
        value={webhook.url}
        onChangeHandler={(value) => setWebhookField("url", value)}
        required
        disabled={FIXED_URL_DELIVERY_METHODS.includes(selectedDeliveryMethod) || disabled}
      />
      {showApiKeyField && (
        <FormField
          label="API Key"
          id="api_key"
          type="password"
          subtext={apiKeySubtext}
          value={webhook.api_key || ""}
          onChangeHandler={(value) => setWebhookField("api_key", value)}
          disabled={disabled}
          required={requireApiKey}
        />
      )}
    </>
  );
};

export default WebhookDeliveryConfig;
